import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "docs-listing__container" }
const _hoisted_2 = { class: "filter" }
const _hoisted_3 = {
  key: 0,
  class: "filter__container"
}
const _hoisted_4 = { class: "filter__title" }
const _hoisted_5 = {
  key: 1,
  class: "filter__box"
}
const _hoisted_6 = {
  key: 0,
  class: "docs-listing__description"
}
const _hoisted_7 = {
  key: 1,
  class: "docs-listing__list"
}
const _hoisted_8 = { key: 2 }
const _hoisted_9 = {
  key: 0,
  class: "text"
}
const _hoisted_10 = {
  key: 2,
  class: "text"
}

import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  ref,
} from 'vue'

import CustomListMenu from '@/app/components/custom.lists/CustomListMenu.vue'
import CustomListEmpty from '@/app/components/custom.lists/CustomListEmpty.vue'
import ListCard from '@/app/components/listing/ListCard.vue'
import DocsSort from '@/app/components/listing/DocsSort.vue'
import DocsSortMobile from '@/app/components/listing/DocsSort.mobile.vue'

import { onBeforeRouteLeave, useRouter } from 'vue-router'

import { CustomListsView, isEditableCustomList } from '@/models/custom.lists'
import type { ListView } from '@/models/docs.list'
import { DocsScroll } from '@/models/docs.scroll'
import { Mixpanel } from '@/analytics/mixpanel'
import { assertNotNull } from '@/helpers/typing'
import type { ListItem } from '@/models/interfaces'
import { DocsView } from '@/models/doc'
import { Sentry } from '@/services/sentry'
import { playerSession } from '@/services/player'


export default /*@__PURE__*/_defineComponent({
  __name: 'ListDocs',
  props: {
    view: {},
    showDocSort: { type: Boolean, default: true },
    listsView: {}
  },
  setup(__props: any) {

const props = __props

const router = useRouter()

const docsScroll = ref<DocsScroll | null>(null)

const lastScrollPosition = ref<number | null>(null)

onMounted(() => {
  if (props.view) {
    props.view.trackSearchResultsBatch(props.view.docs().filteredDocs)
    docsScroll.value = new DocsScroll(props.view, (docs) => {
      props.view.trackSearchResultsBatch(docs)
    })
  }
})

onActivated(async () => {
  // Start the event listeners if they haven't started already.
  if (!docsScroll.value?.isListening) {
    docsScroll.value?.initEventListeners()
  }

  // If there's a stored scroll position, restore it.
  if (lastScrollPosition.value) {
    await nextTick()
    appEl().scrollTop = lastScrollPosition.value
  }
})

onBeforeRouteLeave(() => {
  // Store the scroll position when going to a new page, so it can be
  // restored later when this component is restored.
  //
  // We do this `onBeforeRouteLeave` instead of `onDeactivated` because
  // `onDeactivated` only triggers after the component has already left
  // the DOM, so we can't get the scroll position.
  lastScrollPosition.value = appEl().scrollTop
})

onDeactivated(() => {
  docsScroll.value?.removeScrollEventListener()
})

onBeforeUnmount(() => {
  if (docsScroll.value) {
    docsScroll.value.removeScrollEventListener()
  }
})

/**
 * Get the app container element.
 */
function appEl(): HTMLElement {
  return assertNotNull(document.getElementById('app'))
}

function trackDocView(event: Event, doc: ListItem): void {
  try {
    playerSession.value.setNextArrival('card')
    Mixpanel.trackDocView(
      event,
      props.view.filterTitle(),
      router.currentRoute.value.fullPath,
      doc,
      props.view.docViewEventProperties(doc),
    )
  } catch (error: any) {
    // Unexpected error in the Mixpanel tracking
    Sentry.captureException(error)
  }
}

/**
 * Return whether any type is unchecked in the document-type filter, which
 * means an empty list may not really be empty.
 */
function isAnyDocTypeUnchecked(): boolean {
  const docs = props.view.docs()
  return docs instanceof DocsView && docs.isAnyDocTypeUnchecked
}

/**
 * Return whether or not the current page is a non-standard Custom List listing page.
 */
const showCustomListMenu = computed(() =>
  isEditableCustomList(props.view.customList),
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (__props.view)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("h2", _hoisted_4, _toDisplayString(__props.view.filterTitle()), 1),
            (__props.view.customList && showCustomListMenu.value)
              ? (_openBlock(), _createBlock(CustomListMenu, {
                  key: 0,
                  list: __props.view.customList,
                  listsView: props.listsView
                }, null, 8, ["list", "listsView"]))
              : _createCommentVNode("", true),
            (__props.showDocSort && __props.view.docs().isDocSortVisible())
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(DocsSort, {
                    view: 
              // `isDocSortVisible` only returns true in `DocsView`.
              __props.view.docs() as DocsView
            
                  }, null, 8, ["view"])
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    (__props.view.description())
      ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(__props.view.description()), 1))
      : _createCommentVNode("", true),
    (__props.view.dataLength)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.view.docs().filteredDocs, (doc) => {
            return (_openBlock(), _createBlock(ListCard, {
              key: doc.id,
              view: __props.view,
              doc: doc,
              onClick: ($event: any) => (trackDocView($event, doc))
            }, null, 8, ["view", "doc", "onClick"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (__props.view && !__props.view.dataLength)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          (isAnyDocTypeUnchecked())
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, "No items found."))
            : (__props.view.customList?.doc_type === 'custom_list')
              ? (_openBlock(), _createBlock(CustomListEmpty, { key: 1 }))
              : (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(__props.view.notFoundMessage()), 1))
        ]))
      : _createCommentVNode("", true),
    (__props.view.docs().isDocSortVisible())
      ? (_openBlock(), _createBlock(DocsSortMobile, {
          key: 3,
          view: 
        // `isDocSortVisible` only returns true in `DocsView`.
        __props.view.docs() as DocsView
      
        }, null, 8, ["view"]))
      : _createCommentVNode("", true)
  ]))
}
}

})